import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Stack,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  CheckCircle as ActivateIcon,
  Add as AddIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from "../../../api";
import { showSuccess, showError, showDeleteConfirmation } from '../../../utils/sweetAlertConfig';

const PRIMARY = "#29204C";
const SUCCESS = "#68B977";
const SECONDARY = "#5A6D8B";
const INFO = "#17a2b8";
const WARNING = "#ffc107";

const initialFormData = {
  posting_title: "",
  job_title: "",
  description: "",
  requirements: "",
  responsibilities: "",
  location: "",
  employment_type: "full-time",
  experience_level: "",
  department: "",
  salary_min: "",
  salary_max: "",
  salary_currency: "EGP",
  status: "active",
  deadline: "",
  positions_available: 1,
  requires_professional_info: false
};

export default function Jobs() {
  // State
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalJobs, setTotalJobs] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [viewingJob, setViewingJob] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [selectedTab, setSelectedTab] = useState(0);
  const [search, setSearch] = useState("");

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    draft: 0,
    closed: 0,
  });

  const tabs = [
    { label: 'All', status: '' },
    { label: 'Active', status: 'active' },
    { label: 'Inactive', status: 'inactive' },
    { label: 'Draft', status: 'draft' },
    { label: 'Closed', status: 'closed' },
  ];

  // Load jobs
  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await api.get("/jobs");
      // Handle both response formats: with success flag or direct array
      const jobsData = response.data.success ? response.data.data : response.data;
      const jobsArray = Array.isArray(jobsData) ? jobsData : [];
      setJobs(jobsArray);
      setTotalJobs(jobsArray.length);
      calculateStats(jobsArray);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      showError('Error!', error.response?.data?.message || 'Failed to fetch jobs.');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  // Calculate stats
  const calculateStats = (jobsData) => {
    setStats({
      total: jobsData.length,
      active: jobsData.filter(j => j.status === 'active').length,
      inactive: jobsData.filter(j => j.status === 'inactive').length,
      draft: jobsData.filter(j => j.status === 'draft').length,
      closed: jobsData.filter(j => j.status === 'closed').length,
    });
  };

  // Filter jobs
  const getFilteredJobs = () => {
    // Ensure jobs is always an array
    const jobsArray = Array.isArray(jobs) ? jobs : [];
    let filtered = jobsArray;

    // Filter by status tab
    if (tabs[selectedTab].status) {
      filtered = filtered.filter(j => j.status === tabs[selectedTab].status);
    }

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(j =>
        (j.posting_title && j.posting_title.toLowerCase().includes(searchLower)) ||
        (j.job_title && j.job_title.toLowerCase().includes(searchLower)) ||
        (j.location && j.location.toLowerCase().includes(searchLower)) ||
        (j.department && j.department.toLowerCase().includes(searchLower))
      );
    }

    return filtered;
  };

  const filteredJobs = getFilteredJobs();
  const paginatedJobs = filteredJobs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Handlers
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setPage(0);
  };

  const handleView = (job) => {
    setViewingJob(job);
    setShowViewModal(true);
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      posting_title: job.posting_title || "",
      job_title: job.job_title || "",
      description: job.description || "",
      requirements: job.requirements || "",
      responsibilities: job.responsibilities || "",
      location: job.location || "",
      employment_type: job.employment_type || "full-time",
      experience_level: job.experience_level || "",
      department: job.department || "",
      salary_min: job.salary_min || "",
      salary_max: job.salary_max || "",
      salary_currency: job.salary_currency || "EGP",
      status: job.status || "active",
      deadline: job.deadline ? job.deadline.split('T')[0] : "",
      positions_available: job.positions_available || 1,
      requires_professional_info: job.requires_professional_info || false
    });
    setShowModal(true);
  };

  const handleAddNew = () => {
    setEditingJob(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.posting_title || !formData.job_title) {
      showError('Error!', 'Please fill all required fields.');
      return;
    }

    if (submitting) return; // Prevent double submission

    setSubmitting(true);
    try {
      if (editingJob) {
        await api.put(`/jobs/${editingJob.job_id || editingJob.id}`, formData);
        showSuccess('Updated!', 'Job has been updated successfully.');
      } else {
        await api.post("/jobs", formData);
        showSuccess('Created!', 'Job has been created successfully.');
      }
      setShowModal(false);
      fetchJobs();
    } catch (error) {
      showError('Error!', error.response?.data?.message || 'Failed to save job.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = (job) => {
    showDeleteConfirmation(
      'Archive this job?',
      `The job "${job.posting_title || job.job_title}" will be archived and hidden from public view.`
    ).then((result) => {
      if (result.isConfirmed) {
        api.put(`/jobs/${job.job_id || job.id}`, { status: 'inactive' })
          .then(() => {
            showSuccess('Archived!', 'Job has been archived successfully.');
            fetchJobs();
          })
          .catch(() => {
            showError('Error!', 'Failed to archive job.');
          });
      }
    });
  };

  const handleActivate = (job) => {
    api.put(`/jobs/${job.job_id || job.id}`, { status: 'active' })
      .then(() => {
        showSuccess('Activated!', 'Job has been activated successfully.');
        fetchJobs();
      })
      .catch(() => {
        showError('Error!', 'Failed to activate job.');
      });
  };

  const handleDelete = (job) => {
    showDeleteConfirmation(
      'Delete Permanently?',
      `This will permanently delete "${job.posting_title || job.job_title}". This action cannot be undone!`
    ).then((result) => {
      if (result.isConfirmed) {
        api.delete(`/jobs/${job.job_id || job.id}`)
          .then(() => {
            showSuccess('Deleted!', 'Job has been permanently deleted.');
            fetchJobs();
          })
          .catch(() => {
            showError('Error!', 'Failed to delete job.');
          });
      }
    });
  };

  // Status badge
  const getStatusChip = (status) => {
    const statusConfig = {
      'active': { color: 'success', label: 'Active' },
      'inactive': { color: 'default', label: 'Inactive' },
      'draft': { color: 'warning', label: 'Draft' },
      'closed': { color: 'error', label: 'Closed' },
    };

    const config = statusConfig[status] || { color: 'default', label: status };
    return <Chip label={config.label} color={config.color} size="small" sx={{ fontWeight: 600 }} />;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: PRIMARY, mb: 0.5 }}>
              Jobs Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage job postings and track applications
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchJobs} sx={{ color: SECONDARY }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              sx={{
                bgcolor: SUCCESS,
                '&:hover': { bgcolor: '#5aa866' },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Add New Job
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: PRIMARY, color: '#fff' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {stats.total}
              </Typography>
              <Typography variant="body2">Total Jobs</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: SUCCESS, color: '#fff' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {stats.active}
              </Typography>
              <Typography variant="body2">Active</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: SECONDARY, color: '#fff' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {stats.inactive}
              </Typography>
              <Typography variant="body2">Inactive</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: WARNING, color: '#fff' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {stats.draft}
              </Typography>
              <Typography variant="body2">Draft</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#dc3545', color: '#fff' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {stats.closed}
              </Typography>
              <Typography variant="body2">Closed</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
            },
            '& .Mui-selected': {
              color: PRIMARY,
            },
            '& .MuiTabs-indicator': {
              bgcolor: PRIMARY,
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by title, location, or department..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: SECONDARY }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#fff',
            },
          }}
        />
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: PRIMARY }}>
                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>Job Title</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>Location</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>Department</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>Positions</TableCell>
                <TableCell align="center" sx={{ color: '#ffffff', fontWeight: 700, width: 200 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={60} sx={{ color: PRIMARY }} />
                  </TableCell>
                </TableRow>
              ) : paginatedJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      No jobs found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedJobs.map((job, index) => (
                  <TableRow
                    key={job.id || index}
                    sx={{
                      bgcolor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      '&:hover': { bgcolor: '#f3f6fa' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {job.posting_title || job.job_title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{job.location || 'N/A'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {(job.employment_type || 'N/A').replace('-', ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{job.department || 'N/A'}</Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(job.status)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{job.positions_available || 0}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Copy application link">
                          <IconButton
                            size="small"
                            onClick={() => {
                              // Extract number from job_id (e.g., "ZON_JOB_001" -> "1")
                              const jobIdMatch = job.job_id.match(/\d+$/);
                              const jobNumber = jobIdMatch ? parseInt(jobIdMatch[0], 10) : job.id;
                              const link = `${window.location.origin}/apply?id=${jobNumber}`;
                              navigator.clipboard.writeText(link);
                              showSuccess('Copied!', 'Application link copied to clipboard');
                            }}
                            sx={{ color: INFO }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => handleView(job)}
                            sx={{ color: '#1976d2' }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(job)}
                            sx={{ color: '#ed6c02' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {job.status === 'active' ? (
                          <Tooltip title="Archive">
                            <IconButton
                              size="small"
                              onClick={() => handleArchive(job)}
                              sx={{ color: SECONDARY }}
                            >
                              <ArchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <>
                            <Tooltip title="Activate">
                              <IconButton
                                size="small"
                                onClick={() => handleActivate(job)}
                              sx={{ color: SUCCESS }}
                            >
                              <ActivateIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {job.status === 'inactive' && (
                            <Tooltip title="Delete Permanently">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(job)}
                                sx={{ color: '#d32f2f' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredJobs.length > 0 && (
          <TablePagination
            component="div"
            count={filteredJobs.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 15, 25, 50]}
            sx={{
              borderTop: '1px solid #e0e0e0',
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: '0.875rem',
              },
            }}
          />
        )}
      </Paper>

      {/* Add/Edit Job Modal */}
      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: PRIMARY, color: '#fff', fontWeight: 700 }}>
          {editingJob ? 'Edit Job' : 'Add New Job'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Posting Title *"
                  value={formData.posting_title}
                  onChange={(e) => setFormData({ ...formData, posting_title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Job Title *"
                  value={formData.job_title}
                  onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Requirements"
                  multiline
                  rows={3}
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  placeholder="List the requirements for this position..."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Responsibilities"
                  multiline
                  rows={3}
                  value={formData.responsibilities}
                  onChange={(e) => setFormData({ ...formData, responsibilities: e.target.value })}
                  placeholder="Describe the key responsibilities..."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Employment Type</InputLabel>
                  <Select
                    value={formData.employment_type}
                    label="Employment Type"
                    onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                  >
                    <MenuItem value="full-time">Full Time</MenuItem>
                    <MenuItem value="part-time">Part Time</MenuItem>
                    <MenuItem value="contract">Contract</MenuItem>
                    <MenuItem value="internship">Internship</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Department"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Positions Available"
                  value={formData.positions_available}
                  onChange={(e) => setFormData({ ...formData, positions_available: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Deadline"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Require Professional Info?</InputLabel>
                  <Select
                    value={formData.requires_professional_info ? 'yes' : 'no'}
                    label="Require Professional Info?"
                    onChange={(e) => setFormData({ ...formData, requires_professional_info: e.target.value === 'yes' })}
                  >
                    <MenuItem value="no">No</MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              onClick={() => setShowModal(false)}
              variant="outlined"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{
                bgcolor: SUCCESS,
                '&:hover': { bgcolor: '#5aa866' },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {submitting ? 'Saving...' : (editingJob ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* View Job Modal */}
      <Dialog
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: PRIMARY, color: '#fff', fontWeight: 700 }}>
          Job Details
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {viewingJob && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, mb: 1 }}>
                  {viewingJob.posting_title || viewingJob.job_title}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Location</Typography>
                <Typography variant="body1">{viewingJob.location || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Department</Typography>
                <Typography variant="body1">{viewingJob.department || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Employment Type</Typography>
                <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                  {(viewingJob.employment_type || 'N/A').replace('-', ' ')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>{getStatusChip(viewingJob.status)}</Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Positions Available</Typography>
                <Typography variant="body1">{viewingJob.positions_available || 0}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Requires Professional Info</Typography>
                <Chip
                  label={viewingJob.requires_professional_info ? 'Yes' : 'No'}
                  color={viewingJob.requires_professional_info ? 'success' : 'default'}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              {viewingJob.description && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Description</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{viewingJob.description}</Typography>
                </Grid>
              )}
              {viewingJob.requirements && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Requirements</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{viewingJob.requirements}</Typography>
                </Grid>
              )}
              {viewingJob.responsibilities && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Responsibilities</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{viewingJob.responsibilities}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setShowViewModal(false)}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
